
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomeCare() {
  const [activeTab, setActiveTab] = useState('services');

  const servicesContent = [
    {
      title: '訪問薬剤管理指導',
      description: '患者宅での服薬指導と薬歴管理',
      features: ['服薬状況の確認', '副作用モニタリング', '薬物相互作用チェック', '服薬指導記録作成'],
      icon: 'ri-home-heart-line',
      color: 'bg-teal-50 text-teal-600'
    },
    {
      title: '在宅調剤サービス',
      description: '患者に合わせた調剤・配薬サービス',
      features: ['一包化調剤', '服薬カレンダー', '薬剤配達', '緊急時対応'],
      icon: 'ri-medicine-bottle-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '医療機器管理',
      description: '在宅医療機器の管理とサポート',
      features: ['機器の設置・調整', '使用方法指導', 'メンテナンス', 'トラブル対応'],
      icon: 'ri-stethoscope-line',
      color: 'bg-green-50 text-green-600'
    }
  ];

  const collaborationContent = [
    {
      title: '医師との連携',
      description: '主治医との密な情報共有',
      features: ['処方提案', '副作用報告', '薬物療法モニタリング', '治療方針相談'],
      icon: 'ri-user-heart-line',
      color: 'bg-red-50 text-red-600'
    },
    {
      title: '看護師との連携',
      description: '訪問看護師との協働',
      features: ['服薬状況共有', '症状観察', 'ケアプラン作成', '緊急時連携'],
      icon: 'ri-nurse-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: 'ケアマネジャーとの連携',
      description: 'ケアプラン策定への参画',
      features: ['薬学的視点の提供', 'ADL評価', 'サービス調整', '家族支援'],
      icon: 'ri-team-line',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const equipmentContent = [
    {
      title: '輸液ポンプ',
      description: '持続的な薬液投与装置',
      features: ['流量制御', 'アラーム機能', '安全機構', '操作指導'],
      icon: 'ri-drop-line',
      color: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: '在宅酸素療法',
      description: '酸素濃縮器・液体酸素',
      features: ['機器選択', '安全管理', '使用指導', '定期点検'],
      icon: 'ri-lungs-line',
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      title: '血糖測定器',
      description: '自己血糖測定のサポート',
      features: ['機器選択', '測定指導', 'データ管理', '結果評価'],
      icon: 'ri-pulse-line',
      color: 'bg-pink-50 text-pink-600'
    }
  ];

  const getContentByTab = (tab: string) => {
    switch (tab) {
      case 'services': return servicesContent;
      case 'collaboration': return collaborationContent;
      case 'equipment': return equipmentContent;
      default: return servicesContent;
    }
  };

  const currentContent = getContentByTab(activeTab);

  const patientCases = [
    {
      title: '高齢者の多剤併用',
      description: '複数の慢性疾患を持つ高齢患者への対応',
      challenges: ['服薬管理困難', '薬物相互作用', '副作用リスク', '認知機能低下'],
      solutions: ['一包化調剤', '服薬カレンダー', '家族指導', '定期モニタリング']
    },
    {
      title: 'がん患者の疼痛管理',
      description: '在宅でのがん性疼痛コントロール',
      challenges: ['疼痛評価', '麻薬管理', '副作用対策', '心理的サポート'],
      solutions: ['疼痛スケール活用', '適切な麻薬調剤', '制吐剤併用', 'カウンセリング']
    },
    {
      title: '小児在宅医療',
      description: '医療的ケア児への薬学的サポート',
      challenges: ['体重変動', '服薬困難', '家族負担', '成長発達'],
      solutions: ['個別調剤', '服薬工夫', '家族教育', '発達考慮']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-green-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/5c0767d38d074244d2475ce21a2a043f.jpg"
            alt="在宅医療"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-teal-600">在宅医療</span>サポート
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            患者さんの自宅での薬物療法を多職種連携でサポート
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveTab('services')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'services'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-600'
              }`}
            >
              在宅サービス
            </button>
            <button
              onClick={() => setActiveTab('collaboration')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'collaboration'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-600'
              }`}
            >
              多職種連携
            </button>
            <button
              onClick={() => setActiveTab('equipment')}
              className={`px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'equipment'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-teal-600'
              }`}
            >
              医療機器
            </button>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentContent.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                <div className={`w-16 h-16 rounded-xl ${item.color} flex items-center justify-center mb-6`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Cases */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">症例別アプローチ</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {patientCases.map((patientCase, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{patientCase.title}</h3>
                <p className="text-gray-600 mb-6">{patientCase.description}</p>
                
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-red-600 mb-3">課題</h4>
                  <ul className="space-y-2">
                    {patientCase.challenges.map((challenge, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-alert-line text-red-500 mr-2 mt-1"></i>
                        <span className="text-gray-700 text-sm">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-green-600 mb-3">解決策</h4>
                  <ul className="space-y-2">
                    {patientCase.solutions.map((solution, idx) => (
                      <li key={idx} className="flex items-start">
                        <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                        <span className="text-gray-700 text-sm">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">在宅医療のメリット</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-smile-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">患者の利便性</h3>
              <p className="text-gray-600 text-sm">通院負担の軽減と住み慣れた環境での治療</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">安全性向上</h3>
              <p className="text-gray-600 text-sm">服薬管理の徹底と副作用の早期発見</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">チーム医療</h3>
              <p className="text-gray-600 text-sm">多職種連携による包括的ケア</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">QOL向上</h3>
              <p className="text-gray-600 text-sm">生活の質の向上と家族の安心</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            在宅医療についてのご相談
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            在宅医療の導入や運営についてお気軽にご相談ください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              専門相談
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">在宅医療</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setActiveTab('services')} className="hover:text-white cursor-pointer">在宅サービス</button></li>
                <li><button onClick={() => setActiveTab('collaboration')} className="hover:text-white cursor-pointer">多職種連携</button></li>
                <li><button onClick={() => setActiveTab('equipment')} className="hover:text-white cursor-pointer">医療機器</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/specialty-pharmacy" className="hover:text-white cursor-pointer">専門薬局</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}